// The funicular car, shared by the game (funicular.js) and the dashboard's
// ride preview (objpreview.js → /api/objpreview.js): ONE source, so the
// preview is the car the players ride. `prof` = one livery.json line (or null).
import * as THREE from 'three';
import { mergeParts, box } from './cabinparts.js';
import { classOff } from './device.js';

// a geometry placed into car space like a Mesh at (position, rotation, scale)
const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler();
const _p = new THREE.Vector3();
const _s = new THREE.Vector3();
// glass parts carry no color of their own (the glass material ignores it)
const CLEAR = [1, 1, 1];
function placed(geo, x, y, z, rx = 0, ry = 0, rz = 0, sx = 1, sy = 1, sz = 1) {
  _q.setFromEuler(_e.set(rx, ry, rz));
  return geo.applyMatrix4(_m.compose(_p.set(x, y, z), _q, _s.set(sx, sy, sz)));
}

// single: the glass draws in ONE pass (default the class rule, device.js
// CLASS_OFF glass_2pass: the Quest; the desktop and the dashboard preview keep
// three's back-then-front pair of a transparent DoubleSide material)
export function buildFunicularCar(prof, single = classOff('glass_2pass')) {
  // CLOSED cabin in the REAL livery: body/stripe colors come from the
  // bake's livery.json (AI-profiled per line from the Commons reference
  // photos in .data/refimages) so each railway wears its OWN cars — the
  // Corviglia blue/yellow stays the default for bakes without a plan.
  // Near-full-glass sides between pillars, glazed doors mid-car, rounded
  // ends with a big windscreen. Riders are beamed in/out by the station
  // zones; the glass keeps the track-side posters visible from inside.
  // TWO draws per car (a Quest pays ~35 µs CPU per draw): every opaque part
  // merged into one vertex-colored mesh, every glass part into one
  // transparent mesh. The colors go through the same hex → THREE.Color as the
  // former per-part materials, so each part keeps its exact shade.
  const toHex = (v, dflt) => (Array.isArray(v) && v.length >= 3
    ? new THREE.Color(Math.min(1, Math.max(0, v[0])), Math.min(1, Math.max(0, v[1])),
      Math.min(1, Math.max(0, v[2]))).getHex() : dflt);
  const bodyHex = toHex(prof && prof.body_rgb, 0x1d5fb8);
  const stripeHex = prof && prof.stripe === false ? bodyHex
    : toHex(prof && prof.stripe_rgb, 0xe8c400);
  const rgb = (hex) => new THREE.Color(hex).toArray();
  const blue = rgb(bodyHex), yellow = rgb(stripeHex);
  const dark = rgb(0x2c2e33), rail = rgb(0xc8ccd2);
  const P = [], G = [];           // opaque parts, glass parts
  P.push({ geo: box(2.4, 0.24, 5.6, 0, 0.12, 0), color: dark });       // floor
  P.push({ geo: box(2.6, 0.16, 5.9, 0, 2.62, 0), color: blue });       // roof
  // blue pillars framing the glass
  for (const [px, pz] of [[-1.14, -2.7], [1.14, -2.7], [-1.14, 2.7], [1.14, 2.7],
    [-1.14, -1.0], [1.14, -1.0], [-1.14, 1.0], [1.14, 1.0]]) {
    P.push({ geo: box(0.12, 2.5, 0.12, px, 1.37, pz), color: blue });
  }
  for (const sx of [-1.17, 1.17]) {
    // [0, 2.02] = the former open doorway, now a closed glazed door
    // (boarding is by beam — riders are placed inside the cabin)
    for (const [zc, zl] of [[-1.87, 1.66], [1.87, 1.66], [0, 2.02]]) {
      // blue skirt below the yellow waistline, glass above
      P.push({ geo: box(0.06, 0.72, zl, sx, 0.6, zc), color: blue });
      P.push({ geo: box(0.062, 0.16, zl, sx, 1.04, zc), color: yellow });
      G.push({ geo: box(0.04, 1.3, zl, sx, 1.8, zc), color: CLEAR });
      if (zc === 0) {           // door seam
        P.push({ geo: box(0.065, 2.4, 0.05, sx, 1.3, 0), color: dark });
      }
    }
  }
  // rounded blue ends with windscreen + yellow bumper stripe
  for (const zc of [-2.78, 2.78]) {
    P.push({ geo: box(2.4, 2.5, 0.1, 0, 1.37, zc), color: blue });     // end wall
    P.push({ geo: placed(new THREE.CylinderGeometry(1.2, 1.2, 0.5, 12,
      1, false, zc > 0 ? 0 : Math.PI, Math.PI),
    0, 0.55, zc + (zc > 0 ? 0.1 : -0.1), 0, Math.PI / 2, Math.PI / 2, 0.4, 1, 1),
    color: blue });                                                       // nose
    G.push({ geo: box(1.9, 1.0, 0.04, 0, 1.85, zc + (zc > 0 ? 0.08 : -0.08)),
      color: CLEAR });                                                    // screen
    P.push({ geo: box(2.4, 0.14, 0.11, 0, 1.02, zc), color: yellow });  // bumper
  }
  // chrome handrails inside the doorways
  for (const sx of [-0.95, 0.95]) {
    P.push({ geo: placed(new THREE.CylinderGeometry(0.025, 0.025, 1.6, 6), sx, 1.05, 0),
      color: rail });
  }
  const g = new THREE.Group();
  const body = new THREE.Mesh(mergeParts(P),
    new THREE.MeshLambertMaterial({ vertexColors: true }));
  body.name = 'car-body';
  const glass = new THREE.Mesh(mergeParts(G), new THREE.MeshLambertMaterial({
    color: 0x9fc8e0, transparent: true, opacity: 0.32, side: THREE.DoubleSide, forceSinglePass: single,
  }));
  glass.name = 'car-glass';
  g.add(body, glass);
  return g;
}
